import {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiConfig } from '@/api/config';
import { configureHttp } from '@/api/http';
import type { AuthUser } from '@/api/types';

/**
 * RSV ID v2 SDK integration — direct port of fitsuite/manager useManagerAuth.
 *
 * `isAuthenticated` flips the instant the SDK hands us a user-level JWT.
 * Identity (name/email/phone/uuid/roles/client) comes straight from the
 * SDK state + JWT claims — no follow-up "/me"-style call. Per-tenant Staff
 * role data, when needed, is pulled from existing endpoints like
 * /v3/manager/permissions/account.
 *
 * Lifecycle (mirrors fitsuite):
 *   1. loadSdkScript() ensures `window.RsvIdAuth` is on the page.
 *   2. `onAuth` fires with the user token → resolved, fetch accounts in bg.
 *   3. `onNoAuth` fires → resolved, no auth.
 *   4. `onReady` runs `checkSession()` and acts on the resulting state if
 *      neither onAuth nor onNoAuth already resolved (`resolvedRef`).
 *   5. 6s safety net flips loading off if the SDK never fires anything.
 *
 *   resolvedRef is the single-shot guard. Once flipped, no later handler
 *   can re-trigger the gate — that's what kills the race condition.
 */

const SDK_URL = 'https://id.rsv.mx/sdk/rsv.id.auth.js?v=2';
const SDK_SCRIPT_ID = 'rsv-id-sdk';
const SESSION_ACCOUNT_INDEX = 'rsv-tickets:auth:accountIndex';
const SESSION_CLIENT_TOKEN = 'rsv-tickets:auth:clientToken';

interface RsvIdAuthState {
  isReady?: boolean;
  isAuthenticated?: boolean;
  token?: string | null;
  uuid?: string | null;
  user?: { uuid?: string; email?: string; name?: string; phone?: string } | null;
}

interface RsvIdAuthInstance {
  state?: RsvIdAuthState;
  getToken?: () => string | null;
  login: () => void | Promise<unknown>;
  logout: () => void | Promise<unknown>;
  checkSession?: () => Promise<unknown>;
  onAuth: (cb: (state: RsvIdAuthState) => void) => void;
  onNoAuth: (cb: () => void) => void;
  onReady: (cb: () => void | Promise<void>) => void;
}

interface RsvIdAuthCtor {
  new (config: {
    authDomain: string;
    apiDomain: string;
    context: 'staff' | 'customer' | 'user' | 'public';
    debug?: boolean;
  }): RsvIdAuthInstance;
}

declare global {
  interface Window {
    RsvIdAuth?: RsvIdAuthCtor;
  }
}

export interface AuthAccount {
  index: number;
  token: string;
  client: { uuid: string | null; name: string | null };
  staff: { uuid: string | null; name: string | null };
  roles: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  /** True when the SDK has a user-level JWT. */
  isAuthenticated: boolean;
  /** True only during the initial SDK boot — flips false the moment auth resolves. */
  isLoading: boolean;
  /** True while we're still fetching the list of clients for the user. */
  accountsLoading: boolean;
  accounts: AuthAccount[];
  selectedAccount: AuthAccount | null;
  userToken: string | null;
  /** True only when the staff has access to >1 clients and none is chosen. */
  needsAccountPick: boolean;
  selectAccount: (index: number) => void;
  refreshAccounts: () => Promise<AuthAccount[]>;
  login: () => void;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Decode the payload section of a JWT without verifying the signature —
 * the SDK already validated server-side, here we just want the claims.
 * Returns a plain object with whatever the issuer put in the middle segment.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === 'function' ? atob(padded) : '';
    if (!json) return null;
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function loadSdkScript(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve();
    if (window.RsvIdAuth) return resolve();
    if (document.getElementById(SDK_SCRIPT_ID)) {
      const poll = setInterval(() => {
        if (window.RsvIdAuth) {
          clearInterval(poll);
          resolve();
        }
      }, 50);
      return;
    }
    const tag = document.createElement('script');
    tag.id = SDK_SCRIPT_ID;
    tag.src = SDK_URL;
    tag.async = true;
    tag.onload = () => resolve();
    tag.onerror = () => resolve();
    document.head.appendChild(tag);
  });
}

/**
 * Module-level SDK singleton. React 18 StrictMode double-mounts effects in
 * dev — without this guard we'd construct two `new RsvIdAuth()` instances,
 * each firing its own /token + /token-validation requests against id.rsv.mx.
 * Hoisting the instance out of the component eliminates the duplicates.
 */
let sdkSingleton: RsvIdAuthInstance | null = null;
let sdkSingletonPromise: Promise<RsvIdAuthInstance | null> | null = null;

function getOrCreateSdk(): Promise<RsvIdAuthInstance | null> {
  if (sdkSingleton) return Promise.resolve(sdkSingleton);
  if (sdkSingletonPromise) return sdkSingletonPromise;
  sdkSingletonPromise = (async () => {
    await loadSdkScript();
    if (!window.RsvIdAuth) {
      console.error('[rsv-auth] RsvIdAuth SDK failed to load from', SDK_URL);
      return null;
    }
    if (sdkSingleton) return sdkSingleton;
    sdkSingleton = new window.RsvIdAuth({
      authDomain: 'id.rsv.mx',
      apiDomain: 'api.rsv.mx',
      context: 'user',
      debug: false,
    });
    return sdkSingleton;
  })();
  return sdkSingletonPromise;
}

/**
 * Dedupe `/v3/manager/token` fetches: a single token only needs to resolve
 * its accounts once per page load. Kept at module scope so StrictMode
 * remounts don't double-fire.
 */
let fetchedAccountsToken: string | null = null;
let fetchedAccountsPromise: Promise<AuthAccount[]> | null = null;

/**
 * Tracks whether sdk.checkSession() was already fired this page load.
 * StrictMode remounts attach a fresh `onReady` handler on each mount; we
 * still want only one /token call to id.rsv.mx.
 */
let checkSessionPromise: Promise<unknown> | null = null;

function fetchAccounts(userToken: string): Promise<AuthAccount[]> {
  if (fetchedAccountsToken === userToken && fetchedAccountsPromise) {
    return fetchedAccountsPromise;
  }
  fetchedAccountsToken = userToken;
  fetchedAccountsPromise = doFetchAccounts(userToken);
  return fetchedAccountsPromise;
}

async function doFetchAccounts(userToken: string): Promise<AuthAccount[]> {
  const base = apiConfig.baseUrl.replace(/\/v3$/, '');
  let res: Response;
  try {
    res = await fetch(`${base}/v3/manager/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      },
    });
  } catch (err) {
    console.error('[rsv-auth] /v3/manager/token network error', err);
    return [];
  }
  if (!res.ok) {
    console.error('[rsv-auth] /v3/manager/token →', res.status, res.statusText);
    return [];
  }
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return [];
  }
  const rows = Array.isArray((body as { data?: unknown[] } | null)?.data)
    ? ((body as { data: unknown[] }).data as unknown[])
    : [];
  return rows
    .map((raw: unknown, index: number): AuthAccount | null => {
      const row = (raw ?? {}) as Record<string, unknown>;
      const token = typeof row.token === 'string' ? row.token.trim() : '';
      if (!token) return null;
      const tokenPayload = decodeJwtPayload(token) ?? {};
      const roles = Array.isArray(tokenPayload.roles)
        ? (tokenPayload.roles as unknown[]).map((r) => String(r))
        : [];
      const client = (row.client ?? {}) as Record<string, unknown>;
      const staff = (row.staff ?? {}) as Record<string, unknown>;
      return {
        index,
        token,
        roles,
        client: {
          uuid: typeof client.uuid === 'string' ? client.uuid : null,
          name: typeof client.name === 'string' ? client.name : null,
        },
        staff: {
          uuid: typeof staff.uuid === 'string' ? staff.uuid : null,
          name: typeof staff.name === 'string' ? staff.name : null,
        },
      };
    })
    .filter((a: AuthAccount | null): a is AuthAccount => a !== null);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const sdkRef = useRef<RsvIdAuthInstance | null>(null);
  const tokenRef = useRef<string | null>(null);
  const resolvedRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accounts, setAccounts] = useState<AuthAccount[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  /**
   * Activates a specific account: stamps the client token into the HTTP
   * layer + sessionStorage. We DON'T fetch any /me endpoint here — the
   * SDK already gave us the user's identity (name/email/phone) in
   * `state.user` + the JWT claims, which `hydrateFromTokenAndSdkState`
   * populated when auth resolved. Per-tenant Staff role data, if ever
   * needed, can be pulled from the existing /v3/manager/permissions/account
   * endpoint — no need for a Tkts-specific /me wrapper.
   */
  const activateAccount = useCallback((list: AuthAccount[], index: number) => {
    const account = list[index];
    if (!account?.token) return;
    tokenRef.current = account.token;
    setSelectedIndex(index);
    try {
      sessionStorage.setItem(SESSION_ACCOUNT_INDEX, String(index));
      sessionStorage.setItem(SESSION_CLIENT_TOKEN, account.token);
    } catch {
      /* ignore */
    }
  }, []);

  const selectAccount = useCallback(
    (index: number) => {
      activateAccount(accounts, index);
    },
    [accounts, activateAccount],
  );

  const refreshAccounts = useCallback(async () => {
    const token = sdkRef.current?.state?.token ?? sdkRef.current?.getToken?.() ?? null;
    if (!token) return [];
    fetchedAccountsToken = null;
    fetchedAccountsPromise = null;
    const list = await fetchAccounts(token);
    setAccounts(list);
    if (list.length === 1) activateAccount(list, 0);
    return list;
  }, [activateAccount]);

  useEffect(() => {
    let mounted = true;

    configureHttp({
      getToken: () => tokenRef.current,
      onUnauthorized: () => {
        // Don't clear isAuthenticated here — the SDK still has the user
        // token. Just drop the per-call user state; user can re-pick or
        // hit /v3/manager/token will re-issue.
        setUser(null);
      },
    });

    /**
     * Populate `auth.user` from the SDK state + JWT claims. The SDK already
     * exposes uuid/name/email/phone on `state.user` and the JWT carries
     * roles + client info — no separate identity endpoint needed.
     */
    const hydrateFromTokenAndSdkState = (
      token: string,
      state:
        | {
            user?: { uuid?: string; email?: string; name?: string; phone?: string } | null;
          }
        | undefined,
    ) => {
      const payload = decodeJwtPayload(token) ?? {};
      const sdkUser = state?.user ?? null;
      const pickStr = (...vals: unknown[]) =>
        vals.find((v): v is string => typeof v === 'string' && v !== '') ?? null;
      const id =
        typeof payload.sub === 'number'
          ? payload.sub
          : typeof payload.sub === 'string' && /^\d+$/.test(payload.sub)
            ? Number(payload.sub)
            : 0;
      const clientUuid = pickStr(
        (payload.client as { uuid?: string } | undefined)?.uuid,
      );
      const clientName = pickStr(
        (payload.client as { name?: string } | undefined)?.name,
      );
      const roles = Array.isArray(payload.roles)
        ? (payload.roles as unknown[]).map((r) => String(r))
        : [];
      setUser({
        id,
        uuid: pickStr(sdkUser?.uuid, payload.uuid),
        displayName: pickStr(sdkUser?.name, payload.name),
        email: pickStr(sdkUser?.email, payload.email),
        phone: pickStr(sdkUser?.phone, payload.phone),
        isAdmin: false,
        roles,
        client: { uuid: clientUuid, name: clientName },
      });
    };

    const resolveAccounts = async (userToken: string) => {
      setAccountsLoading(true);
      try {
        const list = await fetchAccounts(userToken);
        if (!mounted) return;
        setAccounts(list);
        if (list.length === 0) {
          // Authenticated but no client membership: that's a server-side
          // permissions problem. UI surfaces this in AccountPicker.
          try {
            sessionStorage.removeItem(SESSION_ACCOUNT_INDEX);
            sessionStorage.removeItem(SESSION_CLIENT_TOKEN);
          } catch {
            /* ignore */
          }
          return;
        }
        if (list.length === 1) {
          activateAccount(list, 0);
          return;
        }
        // Multi-account: try to restore last choice, else show picker.
        const cachedRaw = (() => {
          try {
            return sessionStorage.getItem(SESSION_ACCOUNT_INDEX);
          } catch {
            return null;
          }
        })();
        const cached = cachedRaw !== null ? Number(cachedRaw) : NaN;
        if (Number.isFinite(cached) && cached >= 0 && cached < list.length) {
          activateAccount(list, cached);
        }
      } catch {
        /* swallow — staff still authenticated, AccountPicker shows empty */
      } finally {
        if (mounted) setAccountsLoading(false);
      }
    };

    const init = async () => {
      // Universal `user` context (matches picapie/online-shop and
      // fitsuite/app). It lets BOTH ordinary buyers and staff log in with
      // their RSV ID account — the staff-vs-buyer distinction is later
      // derived by exchanging the token via /v3/manager/token (returns 0
      // accounts for pure buyers, 1+ for staff).
      const sdk = await getOrCreateSdk();
      if (!mounted) return;
      if (!sdk) {
        setIsLoading(false);
        return;
      }
      sdkRef.current = sdk;

      sdk.onAuth((state) => {
        if (!mounted) return;
        resolvedRef.current = true;
        const token = state.token ?? sdk.getToken?.() ?? null;
        setIsAuthenticated(Boolean(token));
        setIsLoading(false); // critical: drop gate the moment we know
        if (token) {
          hydrateFromTokenAndSdkState(token, state);
          void resolveAccounts(token);
        }
      });

      sdk.onNoAuth(() => {
        if (!mounted) return;
        resolvedRef.current = true;
        tokenRef.current = null;
        setIsAuthenticated(false);
        setUser(null);
        setAccounts([]);
        setSelectedIndex(null);
        try {
          sessionStorage.removeItem(SESSION_ACCOUNT_INDEX);
          sessionStorage.removeItem(SESSION_CLIENT_TOKEN);
        } catch {
          /* ignore */
        }
        setIsLoading(false);
      });

      sdk.onReady(async () => {
        if (!mounted) return;
        try {
          if (!checkSessionPromise) {
            checkSessionPromise = Promise.resolve(sdk.checkSession?.());
          }
          await checkSessionPromise;
        } catch (err) {
          if (!resolvedRef.current) {
            console.error('[rsv-auth] sdk.checkSession() threw', err);
            resolvedRef.current = true;
            setIsAuthenticated(false);
            setIsLoading(false);
          }
          return;
        }
        if (!mounted || resolvedRef.current) return;
        const state = sdk.state;
        const token = state?.token ?? sdk.getToken?.() ?? null;
        resolvedRef.current = true;
        if (token) {
          setIsAuthenticated(true);
          setIsLoading(false);
          hydrateFromTokenAndSdkState(token, state);
          void resolveAccounts(token);
        } else {
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      });

      // Safety net: never block the UI forever on a hung SDK.
      setTimeout(() => {
        if (!mounted || resolvedRef.current) return;
        resolvedRef.current = true;
        setIsLoading(false);
      }, 6000);
    };

    void init();

    return () => {
      mounted = false;
    };
  }, [activateAccount]);

  const login = useCallback(() => {
    // No args — the SDK reads window.location itself. Passing
    // { redirectUrl } duplicates the query param and trips id.rsv.mx's
    // allowlist check.
    sdkRef.current?.login();
  }, []);

  const logout = useCallback(async () => {
    try {
      await sdkRef.current?.logout();
    } finally {
      tokenRef.current = null;
      setIsAuthenticated(false);
      setUser(null);
      setAccounts([]);
      setSelectedIndex(null);
      try {
        sessionStorage.removeItem(SESSION_ACCOUNT_INDEX);
        sessionStorage.removeItem(SESSION_CLIENT_TOKEN);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const selectedAccount = selectedIndex !== null ? accounts[selectedIndex] ?? null : null;
  const needsAccountPick =
    isAuthenticated && accounts.length > 1 && selectedIndex === null;

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    accountsLoading,
    accounts,
        selectedAccount,
        userToken: sdkRef.current?.state?.token ?? null,
        needsAccountPick,
        selectAccount,
        refreshAccounts,
        login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
